import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import config from 'constants/apiConfig';
import { selectGroupGuid } from 'slices/commonSelectors';
import { useUscgFocRequirementsFlag } from 'utils/featureFlags';

const ensureLocks = new Map<string, Promise<unknown>>();

type ReplaceResult = { replaced: boolean; count: number };

async function ensureUscgFocAorsForGroup({
  groupGuid,
  queryClient,
}: {
  groupGuid: string;
  queryClient: ReturnType<typeof useQueryClient>;
}): Promise<void> {
  const { getUscgFocAors, toUscgFocAorReplacePayload } = await import(
    'components/MapPage/CorporateViewTabs/AorsTab/utils/uscgFocAors'
  );
  const payload = toUscgFocAorReplacePayload(await getUscgFocAors(groupGuid));

  // Sent as a file: the catalog polygons exceed COMS's 1mb JSON body limit.
  const formData = new FormData();
  formData.append(
    'aors_file',
    new Blob([JSON.stringify(payload)], { type: 'application/json' }),
    'uscg-foc-aors.json'
  );
  const resp = await axios.post<ReplaceResult>(
    config.apiGateway.replaceAorsWithUscgFoc(groupGuid),
    formData
  );

  if (resp.data.replaced) {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['aors', groupGuid] }),
      queryClient.invalidateQueries({
        queryKey: ['aorsByHierarchy', groupGuid],
      }),
    ]);
  }
}

/**
 * When FOC is on, replaces the group's saved AORs with the Coast Guard
 * hierarchy (stable catalog ids) so incidents and AOR reports reference real
 * rows. COMS no-ops once the group already matches the catalog.
 */
export function useEnsureUscgFocAors() {
  const focEnabled = useUscgFocRequirementsFlag();
  const groupGuid = useSelector(selectGroupGuid);
  const queryClient = useQueryClient();
  const completedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!focEnabled || !groupGuid) {
      return;
    }
    if (completedRef.current === groupGuid) {
      return;
    }

    let cancelled = false;
    let ensurePromise = ensureLocks.get(groupGuid);
    if (!ensurePromise) {
      ensurePromise = ensureUscgFocAorsForGroup({ groupGuid, queryClient })
        .catch((error) => {
          console.error(
            '[USCG FOC] Failed to replace AORs with catalog',
            error
          );
          throw error;
        })
        .finally(() => {
          ensureLocks.delete(groupGuid);
        });
      ensureLocks.set(groupGuid, ensurePromise);
    }

    void ensurePromise
      .then(() => {
        if (!cancelled) {
          completedRef.current = groupGuid;
        }
      })
      .catch(() => {
        // Allow retry on next effect.
      });

    return () => {
      cancelled = true;
    };
  }, [focEnabled, groupGuid, queryClient]);
}
